"use client"

import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { Draggable } from "gsap/Draggable"
import sanitizeHtml from 'sanitize-html'
import { useScreenType } from "@/contexts/ScreenTypeContext"
import { InteractiveHoverButton } from "../magicui/interactive-hover-button"

gsap.registerPlugin(Draggable)

export default function CardStack3D({ testimonials }) {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])
  const { screenType } = useScreenType()
  const [cards, setCards] = useState([])

  useEffect(() => {
    if (!testimonials) return

    const sorted = [...testimonials]
      .filter(card => card?.acf?.testimonial_front_page_display)
      .sort((a, b) => {
        const posA = a.acf?.testimonial_position ?? 0
        const posB = b.acf?.testimonial_position ?? 0
        return posA - posB
      })

    setCards(sorted)
  }, [testimonials])

  let depth
  switch (screenType) {
    case "mobile":
      depth = 100
      break
    case "tablet":
      depth = 150
      break
    case "desktop":
    default:
      depth = 200
      break
  }

  const draggableInstance = useRef<Draggable | null>(null)

  const setCardRef = (el: HTMLDivElement | null, index: number) => {
    cardRefs.current[index] = el
  }

  const NUM_CARDS = cards.length

  const animateStack = () => {
    cardRefs.current.forEach((el, i) => {
      if (!el) return
      gsap.to(el, {
        z: -100 * i,
        y: -6 * i,
        scale: 1 - i * 0.05,
        opacity: 1,
        duration: 0.5,
        marginTop: -60 * i + "px",
        ease: "power3.out"
      })
    })
  }

  const rotateCards = () => {
    setCards(prev => {
      if (prev.length === 0) return []
      return [...prev.slice(1), prev[0]]
    })
  }

  const handleNext = () => {
    const frontCard = cardRefs.current[0]
    if (!frontCard) return

    gsap.to(frontCard, {
      x: 0,
      y: 0,
      z: -NUM_CARDS * depth,
      opacity: 0,
      duration: 0.6,
      ease: "power3.inOut",
      onComplete: () => {
        rotateCards()
      }
    })
  }

  useLayoutEffect(() => {
    if (cards.length === 0 || !cardRefs.current[0]) return

    const frontCard = cardRefs.current[0]
    if (!frontCard) return

    if (draggableInstance.current) {
      draggableInstance.current.kill()
    }

    draggableInstance.current = Draggable.create(frontCard, {
      type: "x,y",
      onDragEnd: function () {
        const dragDistance = Math.sqrt(this.x * this.x + this.y * this.y)
        const threshold = 80

        if (dragDistance > threshold) {
          gsap.to(this.target, {
            x: 0,
            y: 0,
            z: -NUM_CARDS * depth,
            opacity: 0,
            duration: 0.6,
            ease: "power3.inOut",
            onComplete: () => {
              rotateCards()
            }
          })
        } else {
          gsap.to(this.target, {
            x: 0,
            y: 0,
            duration: 0.3,
            ease: "power3.out"
          })
        }
      }
    })[0]

    return () => {
      if (draggableInstance.current) {
        draggableInstance.current.kill()
      }
    }
  }, [cards])

  useEffect(() => {
    if (cards.length > 0) {
      animateStack()
    }
  }, [cards])

  return (
    <div className="flex items-center justify-center pb-32 flex-col gap-6">
      <div className="relative w-[360px] md:w-[720px] min-h-[400px] mt-28">
        <div className="absolute inset-0" style={{ perspective: "1200px" }}>
          {cards.map((card, i) => (
            <div
              data-target="drag"
              key={card.id}
              ref={el => setCardRef(el, i)}
              className={`absolute top-1/2 left-1/2 w-full h-full bg-white rounded-2xl shadow-lg flex items-center justify-center text-5xl font-bold text-${card?.acf?.testimonial_card_text_color}`}
              style={{
                transform: `translate3d(-50%, -50%, ${-208 * i}px)`,
                zIndex: NUM_CARDS - i,
                background: getBgColor(card?.acf?.testimonial_card_bg)
              }}
            >
              <div className="flex flex-col p-8">
                <div
                  className="text-lg md:text-[1.65rem] font-normal mb-12 italic"
                  style={{ lineHeight: 2 }}
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtml(card?.title?.rendered || card?.excerpt?.rendered || "")
                  }}
                />
                <div>
                  <div className="text-lg xl:text-xl">
                    {card?.acf?.testimonial_first_name} {card?.acf?.testimonial_last_name}
                  </div>
                  <div className="font-normal text-base xl:text-lg">{card?.acf?.testimonial_work_title}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-x-4 items-center">
        <div onClick={handleNext}>
          <InteractiveHoverButton>Next</InteractiveHoverButton>
        </div>
      </div>
    </div>
  )
}

function getBgColor(color: string | undefined): string {
  switch (color) {
    case "yellow":
      return "#f6c101"
    case "green":
      return "#003831"
    case "lime":
      return "#97ba16"
    case "orange":
      return "#fd7f13"
    case "redpink":
      return "#e04457"
    case "pink":
      return "#ff3e59"
    case "gradient-gg":
      return "linear-gradient(237deg, #aec316 -10%, #003831 100%)"
    case "gradient-gy":
      return "linear-gradient(207deg, #FFC000 -10%, #aec316 10%, #003831 100%)"
    case "gradient-or":
      return "linear-gradient(135deg, #FF3C5C 0%, #ff7318 100%)"
    case "gradient-roy":
      return "linear-gradient(270deg, #FFC000 0%, #FF3C5C 100%)"
    case "gradient-yo":
      return "linear-gradient(119deg, #FFC000 0%, #FF7417 100%)"
    default:
      return "#fff"
  }
}
